// ── Billing container (app-owned data wiring) ──
// Loads the user's billing status, sets up / continues / cancels the mandate, and
// handles the return from the gateway's checkout (?mandate=… → sync, then clean URL).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import BillingPage from './BillingPage.jsx';
import { loadStatus, loadVerifiedPhone, applyOffer, createMandate, syncMandate, cancelMandate, verifyPayment, openMandateCheckout, openOrderCheckout,
  checkSetup, startOverSetup, hasConnectedBroker } from './billing.api.js';
import { chosenInUrl } from './gate.js';
import { loadPlans, loadSubscription, quotePlan, payWhenReady, quoteUpgrade, scheduleDowngrade, cancelDowngrade, quoteAnnualSwitch, quoteRenewal } from './plans.api.js';

const NAV_GRACE_MS = 5000;

// Sent here by the Brokers page's "needs a plan" panel, which stores how many accounts they could
// connect when they left (zs_after_plan): once their plan allows more, take them back with the Add Broker menu open.
// ponytail: checked on page load only (the bank's return reloads this page); a plan that lands
// later by webhook just leaves them here, where the page already says it is active.
async function backToBrokersIfUnlocked() {
  try {
    const left = sessionStorage.getItem('zs_after_plan');
    if (left == null) return;
    const r = await fetch('/api/auth/me', { credentials: 'include' });
    const plan = r.ok ? (await r.json()).plan : null;
    if (plan && (plan.accountCap == null || Number(plan.accountCap) > Number(left))) {
      sessionStorage.removeItem('zs_after_plan');
      window.location.replace('/brokers?add=1');
    }
  } catch (_) { /* stay on billing */ }
}

export default function BillingContainer({ user }) {
  const [status, setStatus] = useState(null);      // null = loading
  const [plans, setPlans] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [quote, setQuote] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [defaultPhone, setDefaultPhone] = useState('');   // the verified mobile, pre-filled
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  // ?plan=pro&billing=annual (pricing page → sign-up → here): picked for them. Read once, on load.
  const [preselect] = useState(() => chosenInUrl());
  const [planOn, setPlanOn] = useState(null);      // plan name, after a purchase completes

  const notify = useCallback((msg, isErr = false) => {
    setToast({ msg, error: isErr });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), isErr ? 7000 : 3500);
  }, []);

  const refresh = useCallback(async () => {
    try { setStatus(await loadStatus()); }
    catch (e) { notify(e.message || 'Could not load billing', true); setStatus({ enabled: false, error: true }); }
    // The catalogue and what they already have. Separately from the mandate status, and
    // separately from each other, so one failing does not blank the whole page.
    // KEEP WHAT WE HAD on a failure. Blanking these on a transient error took a live quote
    // off the screen along with them, and the quote lives only in memory — so the customer
    // lost their place entirely and a reload could not get it back.
    let p = null, s = null;
    try { p = await loadPlans(); setPlans(p); } catch (_) { setPlans((prev) => prev || { plans: [], states: {} }); }
    try { s = await loadSubscription(); setSubscription(s); } catch (_) { /* keep the last known */ }
    return { plans: p, subscription: s };
  }, [notify]);

  useEffect(() => { loadVerifiedPhone().then(setDefaultPhone); }, []);

  // Back from the bank: pull the gateway's view now rather than wait for the webhook,
  // then drop the ?mandate= so a refresh does not sync again.
  useEffect(() => {
    (async () => {
      const q = new URLSearchParams(window.location.search);
      if (q.has('mandate')) {
        try { await syncMandate(); } catch (_) { /* status load below still shows what we have */ }
        try { window.history.replaceState({}, '', '/billing'); } catch (_) { /* fine */ }
      }
      // Back from a plan purchase's payment page (?order= is our own order id, the same whether it was paid,
      // declined or just closed). Ask the gateway what happened now instead of showing "waiting" until a webhook.
      if (q.has('order')) {
        try { await checkSetup(); } catch (_) { /* nothing waiting (a one-time payment, or already settled) */ }
        try { window.history.replaceState({}, '', '/billing'); } catch (_) { /* fine */ }
      }
      const { plans: p, subscription: s } = await refresh();
      await backToBrokersIfUnlocked();
      // Back from paying and the plan is live: point them at the one step that makes it do anything.
      // ponytail: only on this return; a plan that lands later by webhook shows "being set up" instead.
      if ((q.has('mandate') || q.has('order')) && s && s.entitled && s.plan && !(await hasConnectedBroker())) {
        const named = p && p.plans && p.plans.find((x) => x.key === s.plan);
        setPlanOn((named && named.name) || 'Your plan');
      }
    })();
    return () => clearTimeout(toastTimer.current);
  }, [refresh]);

  // The gateway's script is supposed to navigate this tab to the bank. If it resolves
  // and we are still here a few seconds later, the redirect did not happen — give the
  // button back with a message rather than leave the page dead until a reload.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const recoverIfStillHere = useCallback(() => {
    setTimeout(() => {
      if (!mounted.current) return;
      setBusy(false);
      notify('The payment page did not open. Please try again.', true);
    }, NAV_GRACE_MS);
  }, [notify]);

  const onSetup = useCallback(async ({ phone, name, method, billingState }) => {
    setBusy(true);
    try {
      const m = await createMandate({ phone, name, method, billingState });
      await openMandateCheckout({ provider: m.provider, checkout: m.checkout, mode: m.mode, subscriptionId: m.subscriptionId,
        prefill: { email: user && user.email, contact: phone } });                 // navigates away…
      recoverIfStillHere();                                                   // …unless it didn't
    } catch (e) {
      notify(e.message || 'Could not start setup', true);
      setBusy(false);
      refresh();
    }
  }, [notify, refresh, recoverIfStillHere, user]);

  // ── buying a plan ────────────────────────────────────────────────────────
  const onQuote = useCallback(async (choice) => {
    setBusy(true);
    try {
      // AN OFFER CODE in the promo box is applied, not priced: it changes what they get before
      // paying (the card at the top), and there is nothing to quote. Anything else is a discount.
      if (choice.promo && !choice.renew && !choice.switchAnnual && !choice.upgrade) {
        const offer = await applyOffer(choice.promo);
        if (offer) {
          notify(offer.kind === 'trial' ? `Offer applied: ${offer.planName} free for ${offer.days} days. Set up autopay at the top of this page.`
            : `Offer applied: ${offer.planName} free until one of your rules stops you, or for ${offer.days} days. Set up autopay at the top of this page.`);
          await refresh();
          try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { /* fine */ }
          return;
        }
      }
      const out = choice.renew ? await quoteRenewal(choice) : choice.switchAnnual ? await quoteAnnualSwitch(choice)
        : choice.upgrade ? await quoteUpgrade(choice) : await quotePlan(choice);
      // A difference that rounds to nothing is applied by the server at once — there is nothing to pay.
      if (out.applied) { notify('Your plan has been changed.'); refresh(); return; }
      setOrderId(out.orderId);      // the order the quote was written down as; pay() needs exactly this one
      setQuote(out.quote);
    }
    catch (e) { notify(e.message || 'Could not price that', true); }
    finally { setBusy(false); }
  }, [notify, refresh]);

  const onDowngrade = useCallback(async (plan) => {
    setBusy(true);
    try { await scheduleDowngrade(plan); notify('Done — the change happens at your next renewal.'); refresh(); }
    catch (e) { notify(e.message || 'Could not schedule that', true); }
    finally { setBusy(false); }
  }, [notify, refresh]);

  const onCancelDowngrade = useCallback(async () => {
    setBusy(true);
    try { await cancelDowngrade(); notify('You stay on your current plan.'); refresh(); }
    catch (e) { notify(e.message || 'Could not cancel that', true); }
    finally { setBusy(false); }
  }, [notify, refresh]);

  const onPay = useCallback(async ({ phone, method }) => {
    setBusy(true);
    try {
      // payWhenReady waits out a 202 rather than showing it. A second tab getting there first
      // is the lease working, not an error, and the customer should land on the SAME payment
      // page rather than be handed a second one.
      const out = await payWhenReady({ orderId, phone, method });
      // A mandate plan opens the gateway's MANDATE checkout, not its one-off payment one. They
      // are different screens at both gateways, and the payment one cannot authorise a standing
      // permission — the customer would pay once and never be set up to renew.
      if (out.mandate) {
        await openMandateCheckout({
          provider: out.provider, checkout: out.checkout, mode: out.mode, subscriptionId: out.orderId,
          prefill: { email: user && user.email, contact: phone },
        });
      } else {
        await openOrderCheckout({
          provider: out.provider, checkout: out.checkout, mode: out.mode, orderId: out.orderId,
          prefill: { email: user && user.email, contact: phone },
          onVerify: (resp) => verifyPayment(resp),
        });
      }
      recoverIfStillHere();
    } catch (e) {
      notify(e.message || 'Could not start the payment', true);
      setBusy(false);
      // An expired or spent quote cannot be revived; send them back to the choice rather than
      // leaving a Pay button that will keep failing.
      if (['expired', 'session_unknown', 'already_paid'].includes(e.code)) { setQuote(null); setOrderId(null); }
      refresh();
    }
  }, [notify, orderId, recoverIfStillHere, refresh, user]);

  const onContinue = useCallback(async (mandate) => {
    setBusy(true);
    try {
      await openMandateCheckout({ provider: status && status.provider, checkout: mandate.checkout, mode: status && status.mode,
        subscriptionId: mandate.subscriptionId, prefill: { email: user && user.email } });
      recoverIfStillHere();
    } catch (e) { notify(e.message || 'Could not reopen setup', true); setBusy(false); }
  }, [notify, status, recoverIfStillHere, user]);

  // The purchase waiting on "your bank": reopen the same payment page, ask the gateway now, or give up on it
  // to pay another way. Closing the gateway's page tells nobody anything, so these are the only way out.
  const onSetupAction = useCallback(async (what) => {
    const s = subscription && subscription.setupInProgress;
    setBusy(what);
    try {
      if (what === 'continue') {
        await openMandateCheckout({ provider: s && s.provider, checkout: s && s.checkout, mode: s && s.mode,
          subscriptionId: s && s.mandateRef, prefill: { email: user && user.email } });
        recoverIfStillHere();
        return;
      }
      if (what === 'check') {
        const { status: st } = await checkSetup();
        notify(st === 'ACTIVE' ? 'Your bank has approved it. Your plan starts in a moment.'
          : st === 'CANCELLED' ? 'That payment was not completed. Choose a plan to start again.'
          : 'Your bank has not approved it yet. Finish it with Continue payment, or press Start again.');
      } else {
        await startOverSetup();
        notify('Cancelled. Nothing was charged. Choose your plan and how to pay.');
      }
      await refresh();
      setBusy(false);
    } catch (e) { notify(e.message || 'Something went wrong. Please try again.', true); setBusy(false); }
  }, [notify, refresh, recoverIfStillHere, subscription, user]);

  const onCheck = useCallback(async () => {
    setBusy(true);
    try { await syncMandate(); await refresh(); notify('Checked with your bank'); }
    catch (e) { notify(e.message || 'Could not check', true); }
    finally { setBusy(false); }
  }, [notify, refresh]);

  const onCancel = useCallback(async () => {
    if (!window.confirm('Cancel your payment mandate? Nothing more will be charged. You can set up a new one any time.')) return;
    setBusy(true);
    try { await cancelMandate(); await refresh(); notify('Mandate cancelled'); }
    catch (e) { notify(e.message || 'Could not cancel', true); }
    finally { setBusy(false); }
  }, [notify, refresh]);

  return (
    <>
      <BillingPage user={user} status={status} busy={busy} defaultPhone={defaultPhone}
        plans={plans && plans.plans} features={plans && plans.features} states={plans && plans.states} selling={plans && plans.selling} paymentsPaused={plans && plans.paymentsPaused} subscription={subscription}
        quote={quote} onQuote={onQuote} onPay={onPay} onClearQuote={() => { setQuote(null); setOrderId(null); }}
        onDowngrade={onDowngrade} onCancelDowngrade={onCancelDowngrade}
        onSetup={onSetup} onContinue={onContinue} onCheck={onCheck} onCancel={onCancel} onSetupAction={onSetupAction}
        preselect={preselect} planOn={planOn} />
      {toast && <div className={'bl-toast' + (toast.error ? ' error' : '')}>{toast.msg}</div>}
    </>
  );
}
